import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/app/providers/AuthProvider";
import { useToast } from "@/app/providers/ToastProvider";
import { completeSetupWizard } from "@/features/wizard/api";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/Button";
import { PageHeader } from "@/shared/ui/PageHeader";

const STEPS = [
  {
    id: "welcome",
    title: "欢迎",
    body: "Cinema Console 对接本精简 MoviePilot：订阅 → 搜索 → 下载 → 整理 → 绿联。以下步骤帮助你确认核心配置。",
  },
  {
    id: "sites",
    title: "站点",
    body: "配置 PT/索引站点 Cookie，或运行 CookieCloud 同步。",
    link: { to: "/sites", label: "打开站点" },
  },
  {
    id: "download",
    title: "下载器",
    body: "确认 qBittorrent 可用：在下载页应能看到任务列表（空列表也表示连通）。",
    link: { to: "/download", label: "打开下载" },
  },
  {
    id: "library",
    title: "媒体库",
    body: "确认绿联媒体服务器已配置，可在媒体库页查看统计与最新入库。",
    link: { to: "/library", label: "打开媒体库" },
  },
  {
    id: "notify",
    title: "通知",
    body: "按需启用 Telegram / 飞书；密钥请在服务端配置，前端只切换开关。",
    link: { to: "/settings", label: "打开设置" },
  },
  {
    id: "done",
    title: "完成",
    body: "完成后将不再自动弹出向导。你随时可在设置中管理各项配置。",
  },
] as const;

/** 首次登录配置向导：轻量检查清单，不复制整套设置表单 */
export function WizardPage() {
  const { user, clearWizardFlag } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const current = STEPS[step];
  const progress = useMemo(
    () => Math.round(((step + 1) / STEPS.length) * 100),
    [step],
  );

  // 仅当后端标记需要向导时展示；完成后回首页
  if (!user?.wizard) {
    return <Navigate to="/" replace />;
  }

  async function finish() {
    setBusy(true);
    try {
      await completeSetupWizard();
      clearWizardFlag();
      toast("向导已完成", "success");
      navigate("/", { replace: true });
    } catch (e) {
      toast(e instanceof Error ? e.message : "无法保存向导状态", "error");
    } finally {
      setBusy(false);
    }
  }

  function skip() {
    // 跳过也写完成态，避免反复打断；用户可再配
    void finish();
  }

  return (
    <div className="mx-auto max-w-lg">
      <PageHeader
        title="初始配置向导"
        description={`步骤 ${step + 1} / ${STEPS.length}`}
      />

      <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-bg-hover">
        <div
          className="h-full rounded-full bg-brand transition-[width] duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="rounded-[var(--radius-lg)] border border-border bg-bg-card p-5">
        <p className="text-[11px] font-medium uppercase tracking-wider text-brand">
          {current.title}
        </p>
        <p className="mt-3 text-[14px] leading-relaxed text-fg-muted">
          {current.body}
        </p>
        {"link" in current && current.link ? (
          <Link
            to={current.link.to}
            className="mt-4 inline-flex text-[13px] text-brand hover:underline"
          >
            {current.link.label} →
          </Link>
        ) : null}

        <div className="mt-6 flex flex-wrap gap-2">
          {step > 0 ? (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => setStep((s) => s - 1)}
            >
              上一步
            </Button>
          ) : null}
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep((s) => s + 1)}>下一步</Button>
          ) : (
            <Button disabled={busy} onClick={() => void finish()}>
              {busy ? "保存中…" : "完成向导"}
            </Button>
          )}
          <Button
            variant="ghost"
            className={cn("ml-auto")}
            disabled={busy}
            onClick={() => void skip()}
          >
            跳过
          </Button>
        </div>
      </div>
    </div>
  );
}
